import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import './Editor.css';
const EnhancedMarkdownEditor = ({
  value,
  onChange,
  placeholder,
  existingNotes = [],
  className = '',
  style = {},
  onKeyDown,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ top: 0, left: 0 });
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const textAreaRef = useRef(null);
  
  // History management for undo functionality
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Extended markdown suggestion patterns
  const markdownSuggestions = [
    {
      trigger: '#',
      items: [
        { label: 'Heading 1', value: '# Heading 1' },
        { label: 'Heading 2', value: '## Heading 2' },
        { label: 'Heading 3', value: '### Heading 3' },
        { label: 'Heading 4', value: '#### Heading 4' },
        { label: 'Heading 5', value: '##### Heading 5' },
        { label: 'Heading with ID', value: '# Heading {#custom-id}' }
      ]
    },
    {
      trigger: '-',
      items: [
        { label: 'Bullet list', value: '- ' },
        { label: 'Task list', value: '- [ ] ' },
        { label: 'Numbered list', value: '1. ' },
        { label: 'Description list', value: '- Term :: Definition' },
        { label: 'Nested list', value: '  - ' }
      ]
    },
    {
      trigger: '`',
      items: [
        { label: 'Inline code', value: '`code`' },
        { label: 'Code block (JS)', value: '```javascript\n\n```' },
        { label: 'Code block (Python)', value: '```python\n\n```' },
        { label: 'Code block (HTML)', value: '```html\n\n```' },
        { label: 'Code block (CSS)', value: '```css\n\n```' },
        { label: 'Code block (SQL)', value: '```sql\n\n```' },
        { label: 'Code block (TypeScript)', value: '```typescript\n\n```' },
        { label: 'Code block with title', value: '```javascript title="example.js"\n\n```' },
        { label: 'Code block with line numbers', value: '```javascript showLineNumbers\n\n```' },
        { label: 'Code block with highlighted lines', value: '```javascript {1,3-5}\n\n```' }
      ]
    },
    {
      trigger: '>',
      items: [
        { label: 'Blockquote', value: '> ' },
        { label: 'Nested blockquote', value: '>> ' },
        { label: 'Blockquote with author', value: '> Quote\n> — Author' },
        { label: 'Info blockquote', value: '> [!INFO]\n> ' },
        { label: 'Warning blockquote', value: '> [!WARNING]\n> ' },
        { label: 'Note blockquote', value: '> [!NOTE]\n> ' }
      ]
    },
    {
      trigger: '|',
      items: [
        { 
          label: 'Table (2x2)',
          value: '| Header 1 | Header 2 |\n|----------|----------|\n| Cell 1   | Cell 2   |'
        },
        {
          label: 'Table (3x3)',
          value: '| Header 1 | Header 2 | Header 3 |\n|----------|----------|----------|\n| Cell 1   | Cell 2   | Cell 3   |\n| Cell 4   | Cell 5   | Cell 6   |'
        },
        {
          label: 'Table with alignment',
          value: '| Left | Center | Right |\n|:-----|:------:|------:|\n| L1   |   C1   |    R1 |'
        }
      ]
    },
    {
      trigger: ':',
      items: [
        { label: 'Info container', value: '::: info\n\n:::' },
        { label: 'Warning container', value: '::: warning\n\n:::' },
        { label: 'Success container', value: '::: success\n\n:::' },
        { label: 'Error container', value: '::: error\n\n:::' },
        { label: 'Note container', value: '::: note\n\n:::' },
        { label: 'Custom container', value: '::: {.custom-class}\n\n:::' }
      ]
    },
    {
      trigger: '!',
      items: [
        { label: 'Image', value: '![Alt text](image.jpg)' },
        { label: 'Image with title', value: '![Alt text](image.jpg "Image title")' },
        { label: 'Image with size', value: '![Alt text](image.jpg =100x200)' },
        { label: 'Image with link', value: '[![Alt text](image.jpg)](https://link.com)' }
      ]
    },
    {
      trigger: '[',
      items: [
        { label: 'Link', value: '[Link text](https://example.com)' },
        { label: 'Link with title', value: '[Link text](https://example.com "Title")' },
        { label: 'Reference link', value: '[Link text][ref]\n[ref]: https://example.com' },
        { label: 'Footnote', value: '[^footnote]\n\n[^footnote]: Footnote content' }
      ]
    },
    {
      trigger: '$',
      items: [
        { label: 'Inline math', value: '$equation$' },
        { label: 'Display math', value: '$$\n\n$$' },
        { label: 'Matrix', value: '$$\n\\begin{matrix}\n  1 & 2 & 3 \\\\\n  4 & 5 & 6\n\\end{matrix}\n$$' }
      ]
    },
    {
      trigger: '/',
      items: [
        { label: 'Italic', value: '*italic*' },
        { label: 'Bold', value: '**bold**' },
        { label: 'Bold italic', value: '***bold italic***' },
        { label: 'Strikethrough', value: '~~strikethrough~~' },
        { label: 'Highlight', value: '==highlight==' },
        { label: 'Subscript', value: '~subscript~' },
        { label: 'Superscript', value: '^superscript^' }
      ]
    }
  ];

  // Code snippets for common programming patterns
  const codeSnippets = {
    'js': [
      {
        trigger: 'fun',
        items: [
          { 
            label: 'Arrow function',
            value: '() => {\n  \n}'
          },
          {
            label: 'Function declaration',
            value: 'function functionName() {\n  \n}'
          },
          {
            label: 'Async function',
            value: 'async function functionName() {\n  try {\n    \n  } catch (error) {\n    console.error(error);\n  }\n}'
          }
        ]
      },
      {
        trigger: 'log',
        items: [
          { label: 'Console log', value: 'console.log()' },
          { label: 'Console error', value: 'console.error()' },
          { label: 'Console table', value: 'console.table()' }
        ]
      },
      {
        trigger: 'react',
        items: [
          {
            label: 'React component',
            value: 'const ComponentName = () => {\n  return (\n    <div>\n      \n    </div>\n  );\n};'
          },
          {
            label: 'useState hook',
            value: 'const [state, setState] = useState();'
          },
          {
            label: 'useEffect hook',
            value: 'useEffect(() => {\n  \n}, []);'
          }
        ]
      }
    ],
    'python': [
      {
        trigger: 'def',
        items: [
          {
            label: 'Function definition',
            value: 'def function_name():\n    pass'
          },
          {
            label: 'Class definition',
            value: 'class ClassName:\n    def __init__(self):\n        pass'
          }
        ]
      },
      {
        trigger: 'if',
        items: [
          {
            label: 'If statement',
            value: 'if condition:\n    pass\nelse:\n    pass'
          },
          {
            label: 'Try-except',
            value: 'try:\n    pass\nexcept Exception as e:\n    print(f"Error: {e}")'
          }
        ]
      }
    ]
  };

  // Get cursor coordinates for suggestion popup positioning
  const getCursorCoordinates = (textarea, selectionStart) => {
    const textBeforeCursor = textarea.value.substring(0, selectionStart);
    const lines = textBeforeCursor.split('\n');
    const currentLineNumber = lines.length - 1;
    
    // Create a hidden div to measure text dimensions
    const measureDiv = document.createElement('div');
    measureDiv.style.position = 'absolute';
    measureDiv.style.visibility = 'hidden';
    measureDiv.style.whiteSpace = 'pre-wrap';
    measureDiv.style.font = window.getComputedStyle(textarea).font;
    measureDiv.textContent = lines[currentLineNumber];
    document.body.appendChild(measureDiv);
    
    const lineHeight = parseInt(window.getComputedStyle(textarea).lineHeight);
    const { offsetTop, offsetLeft } = textarea;
    const top = offsetTop + (currentLineNumber * lineHeight);
    const left = offsetLeft + measureDiv.offsetWidth;
    
    document.body.removeChild(measureDiv);
    return { top, left };
  };

  // Enhanced suggestion logic
  const getSuggestions = (text, cursorPosition) => {
    const textBeforeCursor = text.substring(0, cursorPosition);
    const lines = textBeforeCursor.split('\n');
    const currentLine = lines[lines.length - 1];
    const words = currentLine.split(/\s/);
    const currentWord = words[words.length - 1];

    // Check if we're in a code block
    const codeBlockMatch = textBeforeCursor.match(/```(\w+)/);
    if (codeBlockMatch) {
      const language = codeBlockMatch[1];
      if (codeSnippets[language]) {
        for (const pattern of codeSnippets[language]) {
          if (currentWord.startsWith(pattern.trigger)) {
            return pattern.items;
          }
        }
      }
    }

    // Internal link suggestions
    if (currentWord.startsWith('[[')) {
      const searchTerm = currentWord.slice(2).toLowerCase();
      return existingNotes
        .filter(note => note.title.toLowerCase().includes(searchTerm))
        .map(note => ({
          label: note.title,
          value: `[[${note.title}]]`
        }));
    }

    // Markdown syntax suggestions
    for (const pattern of markdownSuggestions) {
      if (currentWord.startsWith(pattern.trigger)) {
        return pattern.items;
      }
    }

    return [];
  };

  // Handle textarea input
  const handleInput = (e) => {
    const textarea = e.target;
    const cursorPos = textarea.selectionStart;

    // Call parent onChange
    onChange(e);

    // Save current value to history before changing it
    if (historyIndex === -1 || history[historyIndex] !== textarea.value) {
      setHistory([...history.slice(0, historyIndex + 1), textarea.value]);
      setHistoryIndex(historyIndex + 1);
    }

    // Get and set suggestions
    const newSuggestions = getSuggestions(textarea.value, cursorPos);
    if (newSuggestions.length > 0) {
      const coords = getCursorCoordinates(textarea, cursorPos);
      setCursorPosition(coords);
      setSuggestions(newSuggestions);
      setShowSuggestions(true);
      setSelectedSuggestionIndex(0);
    } else {
      setShowSuggestions(false);
    }

    // Check if a note has been created
    if (textarea.value.trim().endsWith('\n')) {
      setShowSuggestions(false);
    }
  };

  // Handle suggestion selection
  const insertSuggestion = (suggestion) => {
    const textarea = textAreaRef.current;
    const text = textarea.value;
    const cursorPos = textarea.selectionStart;
    const textBeforeCursor = text.substring(0, cursorPos);
    const textAfterCursor = text.substring(cursorPos);
    const words = textBeforeCursor.split(/\s/);
    const currentWord = words[words.length - 1];

    // Create new text with suggestion
    const newText = textBeforeCursor.slice(0, -(currentWord.length)) + 
                    suggestion.value + 
                    textAfterCursor;

    // Update textarea
    const event = {
      target: {
        name: textarea.name,
        value: newText
      }
    };
    onChange(event);

    // Save current value to history before changing it
    setHistory([...history.slice(0, historyIndex + 1), newText]);
    setHistoryIndex(historyIndex + 1);

    // Reset suggestions
    setShowSuggestions(false);

    // Focus back on textarea
    textarea.focus();
  };

  // Handle keyboard navigation and shortcuts
  const handleKeyDown = (e) => {
    if (onKeyDown) {
      onKeyDown(e);
    }

    // Undo functionality with Ctrl+Z
    if (e.ctrlKey && e.key === 'z') {
      e.preventDefault();
      if (historyIndex > 0) {
        setHistoryIndex(historyIndex - 1);
        const previousValue = history[historyIndex - 1];
        const event = {
          target: {
            name: textAreaRef.current.name,
            value: previousValue
          }
        };
        onChange(event);
      }
    }

    // Handle suggestions navigation
    if (showSuggestions) {
      switch (e.key) {
        case 'ArrowDown':
        case 'Tab': // Allow Tab key for navigation
          e.preventDefault();
          setSelectedSuggestionIndex(prev => {
            const newIndex = prev < suggestions.length - 1 ? prev + 1 : prev;
            scrollToSuggestion(newIndex); // Scroll to the new suggestion
            return newIndex;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedSuggestionIndex(prev => {
            const newIndex = prev > 0 ? prev - 1 : prev;
            scrollToSuggestion(newIndex); // Scroll to the new suggestion
            return newIndex;
          });
          break;
        case 'Enter':
          if (suggestions[selectedSuggestionIndex]) {
            e.preventDefault();
            insertSuggestion(suggestions[selectedSuggestionIndex]);
          }
          break;
        case 'Escape':
          setShowSuggestions(false);
          break;
        default:
          break;
      }
    }
  };

  // Function to scroll to the selected suggestion
  const scrollToSuggestion = (index) => {
    const suggestionItems = document.querySelectorAll('.suggestion-item');
    if (suggestionItems[index]) {
      suggestionItems[index].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest', // Scroll to the nearest edge
      });
    }
  };

  // Add a click event listener to the document to hide suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
        if (textAreaRef.current && !textAreaRef.current.contains(event.target)) {
            setShowSuggestions(false);
        }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
        document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="enhanced-markdown-editor" style={{ position: 'relative' }}>
      <textarea
        ref={textAreaRef}
        value={value}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={`enhanced-markdown-textarea ${className}`}
        style={style}
      />
      
      {showSuggestions && (
        <div 
          className="suggestions-popup"
          style={{
            position: 'absolute',
            top: `${cursorPosition.top + 50}px`,
            left: `${cursorPosition.left}px`,
            zIndex: 1000
          }}
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={index}
              className={`suggestion-item ${index === selectedSuggestionIndex ? 'selected' : ''}`}
              onClick={() => insertSuggestion(suggestion)}
              onMouseEnter={() => setSelectedSuggestionIndex(index)}
              onTouchStart={() => insertSuggestion(suggestion)}
            >
              {suggestion.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

EnhancedMarkdownEditor.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  existingNotes: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string.isRequired,
    content: PropTypes.string
  })),
  className: PropTypes.string,
  style: PropTypes.object,
  onKeyDown: PropTypes.func
};

export default EnhancedMarkdownEditor;